import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'

import { Lasku } from '@lemon/core-firebase/model/lasku'
import { FirestoreTosite } from '@lemon/core-firebase/model/tosite'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { TositeUriService } from '@lemon/core-firebase/service/tosite/tosite-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid, FirebaseLemonator } from '../firebase-lemonator.service'

import { KirjanpitoImageHandlerService, TositeJaNaytettavaSivut } from 'app/_angular/service/kirjanpito/kirjanpito.service'
import { LaskuService } from 'app/_angular/service/lasku/lasku.service'
import { LEMONATOR_CF_API, LemonHttpService } from 'app/_angular/service/lemon-http.service'
import { FileSystemFileEntry } from 'app/_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from 'app/_jaettu-angular/file-drop/file-drop-entry'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { KirjanpitoJaettuService } from 'app/_jaettu-lemonator/service/kirjanpito-jaettu.service'
import { ArkistoHallintoUploadDialog, ArkistoHallintoUploadDialogData } from 'app/kirjanpito/hallinto/arkisto-hallinto-upload.dialog'
import {
  KirjattavaKuitti,
  KirjattavaLasku,
  KirjaukseenLiitettyjenTiedostojenSivut,
  KirjaukseenLiitetynTiedostonSivu,
  KirjaukseenLiitetynTiedostonSivunTyyppi,
  KirjauksenKuitinTallennuspyynto,
  KirjauksenKuitinTallennusvastaus,
  KirjauksenLaskunTallennuspyynto,
  KirjauksenRaahattavanTallennuspyynto,
  Kirjaus,
} from '../../../_jaettu-lemonator/model/kirjanpito'
import { AsiakasService, AsiakkaanAvainTiedot } from '../asiakas/asiakas.service'

import { NaytettavaKuitti, NaytettavaLasku, NaytettavaRaahattava } from 'app/kirjanpito/tositteet/tosite-kirjaamattomat.component'
import { firstValueFrom } from 'rxjs'

const KUITIN_TALLENNUKSEN_KAYTTAJALLE_NAKYVAT_VIRHEET: Record<string, string> = {
  'liian-monta-toimittajaa': 'Tositteen liittäminen epäonnistui. Kirjauksessa olisi liian monta toimittajaa.',
} satisfies Partial<Record<KirjauksenKuitinTallennusvastaus['e'], string>>

@Injectable()
export class KirjanpitoTositeService {
  constructor(
    private _firebase: FirebaseLemonator,
    private _kirjanpitoJaettuService: KirjanpitoJaettuService,
    private _kirjanpitoImageHandlerService: KirjanpitoImageHandlerService,
    private _asiakasService: AsiakasService,
    private _lemonHttpService: LemonHttpService,
    private _tositeUriService: TositeUriService,
    private _laskuService: LaskuService,
    private _laskuSharedService: LaskuSharedService,
    private _dateService: DateService,
    private _fileSaverService: FileSaverService,
    private _dialog: MatDialog,
    private _lemonaidFirebase: FirebaseLemonaid,
  ) {}

  async liitaRahaattuKirjaukseen(asiakas: AsiakkaanAvainTiedot, kirjaus: Kirjaus, kirjattavaRaahattava: NaytettavaRaahattava) {
    const tosite = await this._kirjanpitoImageHandlerService.muutaRaahattavaTositteeksi(asiakas, kirjattavaRaahattava)

    const raahattavanSivut: KirjaukseenLiitettyjenTiedostojenSivut = { p: 0, t: {} }
    raahattavanSivut.t[kirjattavaRaahattava.avain] = kirjattavaRaahattava.raahattava.s
    this._kirjanpitoImageHandlerService.tositteenLisaaminenAloitettiin(kirjaus, tosite)

    const lisattavaTiedosto: KirjauksenRaahattavanTallennuspyynto = {
      asiakasAvain: asiakas.avain,
      kirjausAvain: kirjaus.avain,
      raahattavanAvain: kirjattavaRaahattava.avain,
    }

    return this._firebase
      .functionsCall<KirjauksenRaahattavanTallennuspyynto, void>('kirjanpitoKirjauksetRaahattuTallenna', lisattavaTiedosto)
      .then(() => {
        this._kirjanpitoImageHandlerService.tositteenLisaaminenOnnistui(kirjaus, tosite)
      })
      .catch(err => {
        this._kirjanpitoImageHandlerService.tositteenLisaaminenEpaonnistui(kirjaus, tosite)
        throw err
      })
  }

  async liitaKuittiKirjaukseen(asiakas: AsiakkaanAvainTiedot, kirjaus: Kirjaus, kirjattavaKuitti: KirjattavaKuitti): Promise<{ virhe: string }> {
    const tosite = await this._kirjanpitoImageHandlerService.muutaKuittiTositteeksi(asiakas, kirjattavaKuitti)
    const kuitti = kirjattavaKuitti.kuitti[kirjattavaKuitti.kuitti.length - 1]
    const kuitinSivut: KirjaukseenLiitettyjenTiedostojenSivut = { p: 0, t: {} }
    this._kirjanpitoJaettuService.muutaKuittiLiitetyiksiSivuiksi(kuitti, kuitinSivut)
    this._kirjanpitoImageHandlerService.tositteenLisaaminenAloitettiin(kirjaus, tosite)

    const lisattavaTiedosto: KirjauksenKuitinTallennuspyynto = {
      asiakasAvain: asiakas.avain,
      kirjausAvain: kirjaus.avain,
      kuitinAvain: kirjattavaKuitti.avain,
    }

    return this._firebase
      .functionsCall<KirjauksenKuitinTallennuspyynto, KirjauksenKuitinTallennusvastaus>('kirjanpitoKirjauksetKuittiTallennaIlmanYhdistelya', lisattavaTiedosto)
      .then(resp => {
        if (resp?.e) {
          // Käyttäjälle eritellyt virheet
          if (resp.e in KUITIN_TALLENNUKSEN_KAYTTAJALLE_NAKYVAT_VIRHEET) {
            this._kirjanpitoImageHandlerService.tositteenLisaaminenEpaonnistui(kirjaus, tosite)
            return { virhe: KUITIN_TALLENNUKSEN_KAYTTAJALLE_NAKYVAT_VIRHEET[resp.e] }
          }
          // Heitetään normaalina virheenä, jos ei käyttäjälle eritelty virhe
          throw new Error(resp.e)
        }
        this._kirjanpitoImageHandlerService.tositteenLisaaminenOnnistui(kirjaus, tosite)
        return { virhe: null }
      })
      .catch(err => {
        this._kirjanpitoImageHandlerService.tositteenLisaaminenEpaonnistui(kirjaus, tosite)
        throw err
      })
  }

  /** Lataa tositteen kaikki tiedostot */
  async _lataaTositteenTiedostot(tosite: TositeJaNaytettavaSivut): Promise<{ tiedosto: Blob; tiedostonimi: string }[]> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!asiakas || !tosite?.sivut?.length) {
      throw new Error('Asiakas tai tosite puuttuu')
    }

    if (tosite.tyyppi === 'lasku') {
      const laskuSivu = tosite.sivut.find(s => s.sivu.l)
      if (!laskuSivu?.sivu?.l) {
        throw new Error('Laskun tiedot puuttuvat')
      }
      const laskuTiedostot = await this._lataaLaskunTiedostot(asiakas, laskuSivu.sivu.l)
      const liitetiedostot = await this._lataaLaskunLiitetiedostot(asiakas, laskuSivu.sivu.l)
      return [...laskuTiedostot, ...liitetiedostot]
    }

    if (tosite.tyyppi === 'raahattu') {
      const sivu = tosite.sivut[0]
      return [await this._lataaRaahatunTiedosto(asiakas, sivu.avain, sivu.sivu, `tosite_${tosite.avain}`)]
    }

    return [await this._lataaKuitinTiedosto(asiakas, tosite.kuitinAvain)]
  }

  async lataaTositeHandler(tosite: TositeJaNaytettavaSivut): Promise<void> {
    const tiedostot = await this._lataaTositteenTiedostot(tosite)
    for (const { tiedosto, tiedostonimi } of tiedostot) {
      this._fileSaverService.saveAs(tiedosto, tiedostonimi)
    }
  }

  async tallennaHallintokansioonHandler(tosite: TositeJaNaytettavaSivut): Promise<void> {
    const tiedostot = await this._lataaTositteenTiedostot(tosite)
    for (const { tiedosto, tiedostonimi } of tiedostot) {
      await this._avaaTallennaHallintokansioonDialog(tiedosto, tiedostonimi)
    }
  }

  async lataaNaytettavaKuitti(naytettavaKuitti: NaytettavaKuitti): Promise<void> {
    const { tiedosto, tiedostonimi } = await this._lataaNaytettavanKuitinTiedosto(naytettavaKuitti)
    this._fileSaverService.saveAs(tiedosto, tiedostonimi)
  }

  async lataaLasku(naytettavaLasku: NaytettavaLasku): Promise<void> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const lasku = naytettavaLasku.kirjattavaLasku.lasku
    const tiedostot = await this._lataaLaskunTiedostot(asiakas, lasku)
    const liitetiedostot = await this._lataaLaskunLiitetiedostot(asiakas, lasku)
    for (const { tiedosto, tiedostonimi } of [...tiedostot, ...liitetiedostot]) {
      this._fileSaverService.saveAs(tiedosto, tiedostonimi)
    }
  }

  async lataaRaahattava(naytettavaRaahattava: NaytettavaRaahattava): Promise<void> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const { tiedosto, tiedostonimi } = await this._lataaRaahatunTiedosto(
      asiakas,
      naytettavaRaahattava.avain,
      naytettavaRaahattava.raahattava.s,
      naytettavaRaahattava.raahattava.n || `tiedosto_${naytettavaRaahattava.avain}`,
    )
    this._fileSaverService.saveAs(tiedosto, tiedostonimi)
  }

  async tallennaKuittiHallintokansioon(naytettavaKuitti: NaytettavaKuitti): Promise<void> {
    const { tiedosto, tiedostonimi } = await this._lataaNaytettavanKuitinTiedosto(naytettavaKuitti)
    await this._avaaTallennaHallintokansioonDialog(tiedosto, tiedostonimi)
  }

  async tallennaLaskuHallintokansioon(naytettavaLasku: NaytettavaLasku): Promise<void> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const lasku = naytettavaLasku.kirjattavaLasku.lasku
    const tiedostot = await this._lataaLaskunTiedostot(asiakas, lasku)
    const liitetiedostot = await this._lataaLaskunLiitetiedostot(asiakas, lasku)
    for (const { tiedosto, tiedostonimi } of [...tiedostot, ...liitetiedostot]) {
      await this._avaaTallennaHallintokansioonDialog(tiedosto, tiedostonimi)
    }
  }

  async tallennaRaahattavaHallintokansioon(naytettavaRaahattava: NaytettavaRaahattava): Promise<void> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    const { tiedosto, tiedostonimi } = await this._lataaRaahatunTiedosto(
      asiakas,
      naytettavaRaahattava.avain,
      naytettavaRaahattava.raahattava.s,
      naytettavaRaahattava.raahattava.n || `tiedosto_${naytettavaRaahattava.avain}`,
    )
    await this._avaaTallennaHallintokansioonDialog(tiedosto, tiedostonimi)
  }

  async lataaKuitti(asiakasAvain: string, kuitinAvain: string): Promise<FirestoreTosite | null> {
    const tositteenUri = this._tositeUriService.annaTositteenFirestoreUri(asiakasAvain, kuitinAvain)
    return (await this._lemonaidFirebase.firestoreDoc<FirestoreTosite>(tositteenUri).get()) ?? null
  }

  /** Kuitti: haetaan uusin tosite.pdf storagesta (sisältää kaikki kuvat + headerit) */
  private async _lataaKuitinTiedosto(asiakas: AsiakkaanAvainTiedot, kuitinAvain: string): Promise<{ tiedosto: Blob; tiedostonimi: string }> {
    const tositteenUri = this._tositeUriService.annaTositteenFirestoreUri(asiakas.avain, kuitinAvain)
    const firestoreTosite = await this._lemonaidFirebase.firestoreDoc<FirestoreTosite>(tositteenUri).get()
    const kansioUri = this._tositeUriService.annaCloudStoragePdfKansioUriAsiakasIdlle(asiakas.asiakasId + '', firestoreTosite)
    const tiedosto = await this._lemonHttpService.getBinary(`/kuittiLataaAlkuperainen?tositePdf=true&a=${encodeURIComponent('/api/1/kuitit/kuvat/alkuperainen/' + kansioUri)}`, LEMONATOR_CF_API)
    const pvm = this._dateService.muotoileNumberPaiva(firestoreTosite.p, 'fi') || ''
    return { tiedosto, tiedostonimi: `tosite_${pvm}_${firestoreTosite.avain}.pdf` }
  }

  /** Kuitti: ladataan uusin tosite.pdf storagesta (sisältää kaikki kuvat + headerit) */
  private async _lataaNaytettavanKuitinTiedosto(naytettavaKuitti: NaytettavaKuitti): Promise<{ tiedosto: Blob; tiedostonimi: string }> {
    const asiakas = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!asiakas || !naytettavaKuitti.kuitti) {
      throw new Error('Asiakas tai kuitti puuttuu')
    }

    const kansioUri = this._tositeUriService.annaCloudStoragePdfKansioUriAsiakasIdlle(asiakas.asiakasId + '', naytettavaKuitti.kuitti)
    const tiedosto = await this._lemonHttpService.getBinary(`/kuittiLataaAlkuperainen?tositePdf=true&a=${encodeURIComponent('/api/1/kuitit/kuvat/alkuperainen/' + kansioUri)}`, LEMONATOR_CF_API)
    const pvm = this._dateService.muotoileNumberPaiva(naytettavaKuitti.kuitti.p, 'fi') || ''
    return { tiedosto, tiedostonimi: `tosite_${pvm}_${naytettavaKuitti.kuitti.avain}.pdf` }
  }

  /** Lataa laskun PDF:t — juurilasku + liittyvät laskut */
  private async _lataaLaskunTiedostot(asiakas: AsiakkaanAvainTiedot, lasku: Lasku): Promise<{ tiedosto: Blob; tiedostonimi: string }[]> {
    const asiakkaanNimi = lasku.asiakas?.nimi || ''
    const tulokset: { tiedosto: Blob; tiedostonimi: string }[] = []

    tulokset.push(await this._lataaLaskuPdf(asiakas, lasku.avain, `lasku_${lasku.nro}_${asiakkaanNimi}.pdf`))

    const liittyvatLaskut = this._laskuSharedService.annaLiittyvatLaskut(lasku)
    for (const liittyva of liittyvatLaskut) {
      tulokset.push(await this._lataaLaskuPdf(asiakas, liittyva.avain, `lasku_${liittyva.nro}_${asiakkaanNimi}.pdf`))
    }

    return tulokset
  }

  private async _lataaLaskuPdf(asiakas: AsiakkaanAvainTiedot, laskuAvain: string, tiedostonimi: string): Promise<{ tiedosto: Blob; tiedostonimi: string }> {
    const pdfUri = `laskut/${asiakas.asiakasId}/${laskuAvain}.pdf`
    const apiUrl = `/api/1/laskut/lataaPdf/${pdfUri}`
    const cfUrl = `/laskuLataaPdf?a=${encodeURIComponent(apiUrl)}&time=${encodeURIComponent(new Date().getTime())}`
    const tiedosto = await this._lemonHttpService.getBinary(cfUrl, LEMONATOR_CF_API)
    return { tiedosto, tiedostonimi }
  }

  /** Lataa laskun liitetiedostot erikseen — sama logiikka kuin lasku-selaa.katsele lataaLiitetiedosto */
  private async _lataaLaskunLiitetiedostot(asiakas: AsiakkaanAvainTiedot, lasku: Lasku): Promise<{ tiedosto: Blob; tiedostonimi: string }[]> {
    if (!lasku?.liitteet?.length) {
      return []
    }
    const laskuPrefix = `lasku_${lasku.nro}_${lasku.asiakas?.nimi || ''}`
    const tulokset: { tiedosto: Blob; tiedostonimi: string }[] = []
    for (const liitetiedosto of lasku.liitteet) {
      const liitetiedostoUri = this._laskuService.getLiitetiedostoUriAsiakkaalle(asiakas.avain, liitetiedosto)
      const cfUrl = `/laskuLataaLiite?a=${encodeURIComponent('/api/1/laskut/lataaLiitetiedosto/' + liitetiedostoUri)}&time=${encodeURIComponent(new Date().getTime())}`
      const tiedosto = await this._lemonHttpService.getBinary(cfUrl, LEMONATOR_CF_API)
      tulokset.push({ tiedosto, tiedostonimi: `${laskuPrefix}_liite_${liitetiedosto.nimi}` })
    }
    return tulokset
  }

  /** Raahattu: ladataan alkuperäinen tiedosto 'kirjanpitoLataaTosite' funktion kautta */
  private async _lataaRaahatunTiedosto(asiakas: AsiakkaanAvainTiedot, avain: string, sivu: KirjaukseenLiitetynTiedostonSivu, nimiPrefix: string): Promise<{ tiedosto: Blob; tiedostonimi: string }> {
    let storageUri: string
    let tiedostopaate: string

    if (sivu.t === KirjaukseenLiitetynTiedostonSivunTyyppi.RAAHATTU_PDF_RAJAYTETTY_SIVUIKSI || sivu.t === KirjaukseenLiitetynTiedostonSivunTyyppi.RAAHATTU_PDF) {
      storageUri = `${asiakas.avain}/kirjanpito/tositteet/${avain}.pdf`
      tiedostopaate = 'pdf'
    } else if (sivu.t === KirjaukseenLiitetynTiedostonSivunTyyppi.RAAHATTU_WEBP) {
      storageUri = `${asiakas.avain}/kirjanpito/tositteet/${avain}_quality.webp`
      tiedostopaate = 'pdf'
    } else {
      throw new Error('Tuntematon raahatun tiedoston tyyppi: ' + sivu.t)
    }

    const cfUrl = `/kirjanpitoLataaTosite?a=${encodeURIComponent('/api/1/kirjanpito/tosite/' + storageUri)}`
    const tiedosto = await this._lemonHttpService.getBinary(cfUrl, LEMONATOR_CF_API)
    return { tiedosto, tiedostonimi: `${nimiPrefix}.${tiedostopaate}` }
  }

  private async _avaaTallennaHallintokansioonDialog(tiedosto: Blob, tiedostonimi: string): Promise<void> {
    const asiakasData = await firstValueFrom(this._asiakasService.nykyinenAsiakasObservable)
    const tilikaudet = await firstValueFrom(this._asiakasService.nykyisenAsiakkaanTilikaudetObservable)

    const file = new File([tiedosto], tiedostonimi, { type: tiedosto.type })
    const fileEntry: FileSystemFileEntry = {
      name: tiedostonimi,
      isDirectory: false,
      isFile: true,
      file: callback => callback(file),
    }
    const droppedFile = new FileDropEntry(tiedostonimi, fileEntry)

    const data: ArkistoHallintoUploadDialogData = {
      asiakas: asiakasData,
      tilikaudet: tilikaudet || [],
      valittuTilikausiAvain: null,
      droppedFile,
    }

    this._dialog.open(ArkistoHallintoUploadDialog, { data })
  }

  async liitaLaskuKirjaukseen(asiakas: AsiakkaanAvainTiedot, kirjaus: Kirjaus, kirjattavaLasku: KirjattavaLasku) {
    const tosite = await this._kirjanpitoImageHandlerService.muutaLaskuTositteeksi(asiakas, kirjattavaLasku)

    const lasku = this._kirjanpitoJaettuService.annaLaskuKirjattavastaLaskusta(kirjattavaLasku)
    const kuitinSivut: KirjaukseenLiitettyjenTiedostojenSivut = { p: 0, t: {} }
    this._kirjanpitoJaettuService.muutaLaskuLiitetyiksiSivuiksi(lasku, kuitinSivut)
    this._kirjanpitoImageHandlerService.tositteenLisaaminenAloitettiin(kirjaus, tosite)

    const lisattavaTiedosto: KirjauksenLaskunTallennuspyynto = {
      asiakasAvain: asiakas.avain,
      kirjausAvain: kirjaus.avain,
      laskuAvain: kirjattavaLasku.avain,
    }

    return this._firebase
      .functionsCall<KirjauksenLaskunTallennuspyynto, void>('kirjanpitoKirjauksetLaskutTallenna', lisattavaTiedosto)
      .then(() => {
        this._kirjanpitoImageHandlerService.tositteenLisaaminenOnnistui(kirjaus, tosite)
      })
      .catch(err => {
        this._kirjanpitoImageHandlerService.tositteenLisaaminenEpaonnistui(kirjaus, tosite)
        throw err
      })
  }
}
